import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@/modules/common/application/context/user';
import { sendMessageToSlack } from '@/services/slack.service';

interface ReportIssueDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  messageId?: string;
}

const FEEDBACK_TYPES = [
  { value: 'general', label: 'general' },
  { value: 'bug', label: 'bug' },
  { value: 'safety', label: 'safety' },
];

export function ReportIssueDialog({ open, onOpenChange, messageId }: ReportIssueDialogProps) {
  const { t } = useTranslation();
  const { user } = useUser();
  const [feedbackType, setFeedbackType] = useState<string>('');
  const [feedbackText, setFeedbackText] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const handleSubmit = async () => {
    if (!feedbackType) {
      toast.error(t('modules.mavue2.chat.selectFeedbackType'));
      return;
    }

    if (!feedbackText.trim()) {
      toast.error(t('modules.mavue2.chat.provideFeedback'));
      return;
    }

    setIsSubmitting(true);

    try {
      await sendMessageToSlack(
        `Message Feedback: ${feedbackType}
User ${user?.firstName} ${user?.lastName}
Message ID: ${messageId}
Feedback: ${feedbackText}`
      );

      toast.success(t('modules.mavue2.chat.feedbackSubmitted'));
      handleClose();
    } catch {
      toast.error(t('modules.mavue2.chat.feedbackSubmitFailed'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFeedbackType('');
    setFeedbackText('');
    setIsSubmitting(false);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{t('modules.mavue2.chat.reportIssue')}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="feedback-type" className="text-sm font-medium">
              {t('modules.mavue2.chat.feedbackType')}
            </Label>
            <Select value={feedbackType} onValueChange={setFeedbackType}>
              <SelectTrigger id="feedback-type">
                <SelectValue placeholder={t('modules.mavue2.chat.selectReportType')} />
              </SelectTrigger>
              <SelectContent>
                {FEEDBACK_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>
                    {t(`modules.mavue2.chat.feedbackTypes.${type.label}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback-text" className="text-sm font-medium">
              {t('modules.mavue2.chat.yourFeedback')}
            </Label>
            <Textarea
              id="feedback-text"
              placeholder={t('modules.mavue2.chat.feedbackPlaceholder')}
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              className="min-h-[120px] resize-none"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            {t('modules.mavue2.common.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? t('modules.mavue2.common.sending') : t('modules.mavue2.common.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
